import { service } from '@ember/service'
import { Tracker } from 'customer-flows/services/trackers/tracker'
import { context } from 'customer-flows/utils/core'
import uid from 'customer-flows/utils/uid'
import { capitalize } from 'customer-flows/utils/string'
import type GtmTracker from 'customer-flows/services/trackers/gtm-tracker'
import type MetaTracker from 'customer-flows/services/trackers/meta-tracker'

export default class MarketingTracker extends Tracker {
  @service('trackers/gtm-tracker') gtmTracker: GtmTracker
  @service('trackers/meta-tracker') metaTracker: MetaTracker

  @context('program') isMultiProductFlow: ProgramStore['isMultiProductFlow']
  @context('program/tiles') tiles: ProgramTilesStore['tiles']
  @context('activity') activity: ActivityStore['activity']
  @context('activity') tickets: ActivityStore['tickets']
  @context('tickets') getLowestPriceForTicket: TicketsStore['getLowestPriceForTicket']
  @context('tickets') resourceOptionQuantities: TicketsStore['resourceOptionQuantities']
  @context('quote') priceBreakdown: QuoteStore['priceBreakdown']
  @context('quote/booking-quote') getLines: BookingQuoteStore['getLines']
  @context('quote') promoCodes: QuoteStore['promoCodes']
  @context('customer') optinSms: CustomerStore['optinSms']
  @context('customer') optinMarketing: CustomerStore['optinMarketing']

  protected readonly supportedEvents: TrackingEventName[] = [
    'Start',
    'view_items_list',
    'select_item',
    'add_to_cart',
    'remove_from_cart',
    'add_contact_info',
    'add_payment_info',
    'purchase',
    'begin_checkout',
    'select_promotion',
    'Complete'
  ]
  private readonly eventHandlers = {
    Start: (_props: EventProps<'Start'>) => this.trackStart(),
    view_items_list: (_props: EventProps<'view_items_list'>) => this.trackViewItemsList(),
    select_item: (_props: EventProps<'select_item'>) => this.trackedSelectItem(),
    add_to_cart: (props: { ticketId: string, quantity?: number, rawData?: MarketingItemVariant }) => this.trackAddToCart(props),
    remove_from_cart: (props: { ticketId: string, quantity?: number, rawData?: MarketingItemVariant }) => this.trackRemoveFromCart(props),
    add_contact_info: (_props: EventProps<'add_contact_info'>) => this.trackAddContactInfo(),
    add_payment_info: (_props: EventProps<'add_payment_info'>) => this.trackAddPaymentInfo(),
    begin_checkout: (_props: EventProps<'begin_checkout'>) => this.trackBeginCheckout(),
    select_promotion: (props: { quote: Quote }) => this.trackSelectPromotion(props),
    Complete: (_props: EventProps<'Complete'>) => this.trackPurchase()
  }

  registeredEvents: Partial<Record<TrackingEventName, Function[]>> = {}

  private readonly eventStore: Partial<Record<TrackingEventName, EventProps<TrackingEventName>[]>> = {}

  setup(): void {
    this.gtmTracker.setup()
    this.metaTracker.setup()
  }

  track<E extends TrackingEventName>(event: E, props?: EventProps<E>): void {
    const handler = this.eventHandlers[event as keyof typeof this.eventHandlers]

    if (handler) {
      handler(props)
    } else {
      throw new Error(`No tracking method found for event: ${event}`)
    }
  }

  subscribe(eventName: TrackingEventName, callback: Function) {
    if (typeof eventName !== 'string') {
      throw new TypeError('eventName must be a string when registering a listener')
    } else if (!this.supportedEvents.includes(eventName)) {
      const events = this.supportedEvents.join(', ')
      throw new Error(`"${eventName}" is not a valid event; must be one of: ${events}.`)
    } else if (typeof callback !== 'function') {
      throw new TypeError('callback must be a function when registering a listener')
    }

    const listeners = this.registeredEvents[eventName] ?? []

    /**
     * Prevent duplicate callback registrations: only add this specific callback if it's not already registered
     * This ensures the same callback function isn't called multiple times when the event fires
     */
    if (!listeners.includes(callback)) {
      listeners.push(callback)
      this.registeredEvents[eventName] = listeners
    }
  }

  unsubscribe(): void {} // for unmounting apps

  emit<E extends TrackingEventName>(eventName: E, data: EventProps<E>) {
    this.storeEvent(eventName, data)

    const listeners = this.registeredEvents[eventName] ?? []
    listeners.forEach((listener) => listener(data))
  }

  getEvents<E extends TrackingEventName>(eventName: E): EventProps<E>[] {
    return (this.eventStore[eventName] ?? []) as EventProps<E>[]
  }

  private storeEvent<E extends TrackingEventName>(eventName: E, payload: EventProps<E>): void {
    const eventsForType = (this.eventStore[eventName] ?? []) as EventProps<TrackingEventName>[]

    eventsForType.push(payload as EventProps<TrackingEventName>)

    this.eventStore[eventName] = eventsForType
  }

  trackStart(): void {
    if (this.isMultiProductFlow) {
      this.trackViewItemsList()
    } else {
      this.trackedSelectItem()
    }
  }

  trackedSelectItem(): void {
    const timestamp = new Date().toISOString()
    const eventUuid = uid()

    if (!this.activity) return

    const data: SelectItemData = {
      event: 'select_item',
      uuid: eventUuid,
      timestamp: timestamp,
      ecommerce: {
        items: [
          {
            item_id: this.activity.id,
            item_name: this.activity.name,
            item_category: capitalize(this.activity.type),
            quantity: 1,
            coupon: null as string | null,
            price: null as number | null,
            affiliation: 'Peek'
          }
        ]
      }
    }

    this.emit('select_item', data)
  }

  trackViewItemsList(): void {
    const timestamp = new Date().toISOString()
    const eventUuid = uid()

    const data: ViewItemsListData = {
      event: 'view_items_list',
      uuid: eventUuid,
      timestamp: timestamp,
      ecommerce: {
        items: this.tiles
          .map((tile) => {
            const { program } = tile

            if ('activity' in program) {
              return {
                item_id: program.activity.id,
                item_name: program.activity.name,
                item_category: capitalize(program.activity.type),
                quantity: 1,
                coupon: null as string | null,
                price: null as number | null,
                affiliation: 'Peek'
              }
            }

            // TODO: implement other program types
            return undefined
          })
          .filter((item): item is MarketingItem => item !== undefined)
      }
    }

    this.emit('view_items_list', data)
  }

  private buildCartItem(ticketId: string, quantity?: number, rawData?: MarketingItemVariant): MarketingItemVariant | null {
    if (rawData) {
      return rawData
    }

    const ticket = this.tickets.find((ticket) => ticket.id === ticketId)
    if (!ticket || !this.activity || quantity === undefined || quantity === 0) {
      return null
    }

    return {
      item_id: this.activity.id,
      item_name: this.activity.name,
      item_category: 'Ticket',
      item_variant: ticket.name,
      quantity,
      coupon: null as string | null,
      price: parseFloat(this.getLowestPriceForTicket(ticketId)?.amount ?? '0'),
      affiliation: 'Peek'
    }
  }

  trackAddToCart({ ticketId, quantity, rawData }: { ticketId: string, quantity?: number, rawData?: MarketingItemVariant }): void {
    const item = this.buildCartItem(ticketId, quantity, rawData)
    if (!item) return

    const data: AddToCartData = {
      event: 'add_to_cart',
      uuid: uid(),
      timestamp: new Date().toISOString(),
      ecommerce: { items: [item] }
    }

    this.emit('add_to_cart', data)
  }

  trackRemoveFromCart({ ticketId, quantity, rawData }: { ticketId: string, quantity?: number, rawData?: MarketingItemVariant }): void {
    const item = this.buildCartItem(ticketId, quantity, rawData)
    if (!item) return

    const data: RemoveFromCartData = {
      event: 'remove_from_cart',
      uuid: uid(),
      timestamp: new Date().toISOString(),
      ecommerce: { items: [item] }
    }

    this.emit('remove_from_cart', data)
  }

  trackAddContactInfo(): void {
    const timestamp = new Date().toISOString()
    const eventUuid = uid()
    const data: AddContactInfoData = {
      event: 'add_contact_info',
      uuid: eventUuid,
      timestamp: timestamp,
      consent: this.consentData
    }

    this.emit('add_contact_info', data)
  }

  trackAddPaymentInfo(): void {
    const timestamp = new Date().toISOString()
    const eventUuid = uid()

    const data: AddPaymentInfoData = {
      event: 'add_payment_info',
      uuid: eventUuid,
      timestamp: timestamp,
      ecommerce: {
        value: parseFloat(this.priceBreakdown?.total?.amount ?? '0'),
        currency: this.priceBreakdown?.total?.currency ?? 'USD',
        coupon: null,
        items: this.selectedTicketsData
      },
      consent: this.consentData
    }

    this.emit('add_payment_info', data)
  }

  trackPurchase(): void {
    const timestamp = new Date().toISOString()
    const eventUuid = uid()
    const data: PurchaseData = {
      event: 'purchase',
      uuid: eventUuid,
      timestamp: timestamp,
      ecommerce: {
        value: parseFloat(this.priceBreakdown?.total?.amount ?? '0'),
        currency: this.priceBreakdown?.total?.currency ?? 'USD',
        coupon: null,
        items: this.selectedTicketsData
      },
      consent: this.consentData
    }

    this.emit('purchase', data)
  }

  trackBeginCheckout(): void {
    const timestamp = new Date().toISOString()
    const eventUuid = uid()

    const data: BeginCheckoutData = {
      event: 'begin_checkout',
      uuid: eventUuid,
      timestamp: timestamp,
      ecommerce: {
        currency: this.priceBreakdown?.total?.currency ?? 'USD',
        value: parseFloat(this.priceBreakdown?.total?.amount ?? '0'),
        coupon: null as string | null,
        items: this.selectedTicketsData
      }
    }

    this.emit('begin_checkout', data)
  }

  trackSelectPromotion({ quote }: { quote: Quote }): void {
    const timestamp = new Date().toISOString()
    const eventUuid = uid()

    const appliedPromoCode = quote.promoCodes?.find((code) => code.isApplied)
    if (!appliedPromoCode?.code) return

    const data: SelectPromotionData = {
      event: 'select_promotion',
      uuid: eventUuid,
      timestamp: timestamp,
      ecommerce: {
        promotion_id: appliedPromoCode.code,
        promotion_name: appliedPromoCode.code,
        items: this.selectedTicketsData
      }
    }

    this.emit('select_promotion', data)
  }

  get consentData(): ConsentData {
    return {
      optin_marketing: this.optinMarketing ?? false,
      optin_sms: this.optinSms ?? false
    }
  }

  get selectedTicketsData(): MarketingItemVariant[] {
    return (this.getLines() ?? []).map((line) => {
      let itemName = this.activity?.name ?? ''
      let itemVariant = line.tickets?.[0]?.name ?? line.description ?? ''

      if (line.isChild) {
        itemName = line.description?.replace(` - ${itemVariant}`, '') ?? ''
      }

      return {
        item_id: line.sku ?? '',
        item_name: itemName ?? '',
        item_category: line.isChild ? 'Add On' : 'Ticket',
        item_variant: itemVariant,
        quantity: line.quantity ?? 1,
        coupon: null as string | null,
        price: this.priceForTicket(line.sku as string),
        affiliation: 'Peek'
      }
    })
  }

  get priceForTicket(): (_ticketId: string) => number {
    return (id: string) => {
      const quoteLines = this.getLines()

      if (quoteLines.length) {
        const ticket = quoteLines.find((line) => line.sku === id)
        return parseFloat(ticket?.priceBreakdown?.price?.amount ?? '0') / (ticket?.quantity ?? 1)
      }

      return 0
    }
  }
}
